// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// F2 · Os requisitos do pedido, em texto livre
// ============================================================================
//
// 📗 fonte 5.3.3.1 / RF-6: o filtro F2 é *«características exigidas»*. O motor declarava-o
// **não aplicado**: *«os requisitos do pedido são texto livre; casá-los com a lista de
// características exigiria adivinhar»*.
//
// ⚠️ MEDI ANTES DE ESCREVER, E O RESULTADO MUDA O DESENHO
//   O gerador produz **10 tipos** de requisito em 7 918 pedidos, e as viaturas têm **7**
//   características. Cruzando os dois:
//
//   | Requisito | Cabe? |
//   |---|---|
//   | `Wi-Fi`, `frigorífico` | ✅ casam exactamente — 2 de 10 |
//   | `WC a bordo` → `WC` · `ar condicionado reforçado` → `A/C` | ⚠️ **casam por SINÓNIMO**, e a comparação de texto não os apanha |
//   | `condutor com inglês` | ⚠️ **não é da viatura — é do MOTORISTA.** Nenhuma característica de frota o pode satisfazer |
//   | `cintos de 3 pontos`, `bagageira ampla`, `microfone para guia`, `lugares para cadeira de rodas`, `autocarro de 2 pisos` | ⚠️ **NÃO EXISTEM NOS DADOS.** 5 de 10 |
//
// ⚠️ A CONCLUSÃO É A PARTE IMPORTANTE, E CONTRARIA O QUE EU DISSE ANTES
//   **A IA não resolve o F2 sozinha, e afirmá-lo era vender fumo.** Metade dos requisitos
//   refere atributos que a frota não registra — e **nenhum modelo inventa dados que não
//   existem**. Um modelo a decidir sobre isso produziria a resposta mais convincente
//   possível: «esta viatura serve», sem base nenhuma.
//
//   Logo o F2 tem TRÊS resultados e não dois:
//
//     ✅ satisfeito       a característica existe e a viatura tem-na
//     ❌ não satisfeito   a característica existe e a viatura NÃO tem  → EXCLUI
//     ⚠️ indeterminado    não há dados para verificar                  → AVISA, não exclui
//
//   É a mesma disciplina de D-087 (o contador que diz «não é possível documentar» em 26 %
//   dos casos) e de `naoSomado` na função de custo: **separar «não» de «não sei» é o que
//   distingue um sistema em que se confia de um que se aprende a ignorar.**
//
//   E excluir por `indeterminado` seria pior: com 5 de 10 requisitos indetermináveis,
//   **a frota inteira era excluída** e o motor devolvia «não há opções» com 250 viaturas
//   livres.
//
// 📗 ONDE A IA ENTRA, E É MAIS MODESTO DO QUE EU DISSE
//   Na coluna dos **sinónimos** — e a fonte 6.7 já pede uma tabela `sinonimo`, validada
//   com números (1 471 grafias de localidade, 735 de motorista). O modelo **propõe** a
//   entrada; uma pessoa confirma-a; o casamento passa a ser determinístico e explicável.
//   A IA nunca está no caminho da decisão. Ver D-088.
// ============================================================================
/** `WC a bordo` → `wc a bordo`. Minúsculas, sem acentos, sem espaços a mais. */
export function normalizarTexto(t) {
    return t
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Divide o campo de requisitos em requisitos individuais.
 *
 * ⚠️ Aceita `|`, `;` e `,` como separadores porque **os três aparecem** em texto escrito
 * por pessoas — e um separador não previsto transforma três requisitos num só, que depois
 * não casa com nada e cai em `indeterminado` sem razão aparente.
 */
export function separarRequisitos(campo) {
    // ⚠️ 2026-10-23 · um separador DENTRO de parênteses não parte — «acesso fácil (degrau baixo,
    //    se possível)» é um requisito, e partido ao meio ia para as notas em dois bocados. Com os
    //    parênteses desequilibrados não se sabe onde a frase acaba: volta à divisão simples.
    const simples = () => campo.split(/[|;,]/).map((t) => t.trim()).filter((t) => t !== '');
    const partes = [];
    let actual = '';
    let fundo = 0;
    for (const ch of campo) {
        if (ch === '(')
            fundo += 1;
        if (ch === ')')
            fundo -= 1;
        if (fundo < 0)
            return simples();
        if (fundo === 0 && (ch === '|' || ch === ';' || ch === ',')) {
            partes.push(actual);
            actual = '';
        }
        else
            actual += ch;
    }
    if (fundo !== 0)
        return simples();
    partes.push(actual);
    return partes.map((t) => t.trim()).filter((t) => t !== '');
}
/**
 * Normaliza um requisito contra a tabela de sinónimos.
 *
 * ⚠️ Tenta o texto inteiro **e depois** cada palavra com mais de três letras. `WC a
 * bordo` casa por `wc`; `ar condicionado reforçado` casa pela entrada inteira. Sem a
 * segunda tentativa, metade dos sinónimos teria de ser escrita à mão por cada variação de
 * frase — e é isso que a tabela existe para evitar.
 */
export function normalizarRequisito(texto, sinonimos) {
    const chave = normalizarTexto(texto);
    const directo = sinonimos.get(chave);
    if (directo !== undefined) {
        return { texto, caracteristica: directo.caracteristica, alvo: directo.alvo };
    }
    for (const palavra of chave.split(' ')) {
        if (palavra.length < 2)
            continue;
        const achado = sinonimos.get(palavra);
        if (achado !== undefined) {
            return { texto, caracteristica: achado.caracteristica, alvo: achado.alvo };
        }
    }
    return { texto, caracteristica: null, alvo: 'desconhecido' };
}
/**
 * Fase 1 · filtro F2 — avalia os requisitos de um pedido contra uma viatura.
 *
 * ⚠️ **Só exclui por `nao_satisfeito`.** O `indeterminado` avisa e deixa passar, porque
 * com 5 de 10 requisitos indetermináveis excluir por dúvida esvaziava a frota — e o motor
 * devolveria «não há opções» com 250 viaturas livres. É D-007 aplicado a dados em falta:
 * **avisa mas permite.**
 */
export function avaliarF2(requisitosDoPedido, caracteristicasDaViatura, sinonimos) {
    const tem = new Set(caracteristicasDaViatura.map(normalizarTexto));
    const saida = [];
    for (const bruto of separarRequisitos(requisitosDoPedido)) {
        const n = normalizarRequisito(bruto, sinonimos);
        if (n.caracteristica === null) {
            saida.push({
                texto: bruto,
                resultado: 'indeterminado',
                porque: 'não há característica registada que corresponda a este pedido',
            });
            continue;
        }
        if (n.alvo === 'motorista') {
            // ⚠️ `condutor com inglês` não se verifica na frota, e dizer «satisfeito» seria
            // mentir. Fica indeterminado até haver competências do motorista nos dados.
            saida.push({
                texto: bruto,
                resultado: 'indeterminado',
                porque: `é um requisito do MOTORISTA (${n.caracteristica}) e a frota não o pode satisfazer`,
            });
            continue;
        }
        const satisfeito = tem.has(normalizarTexto(n.caracteristica));
        saida.push({
            texto: bruto,
            resultado: satisfeito ? 'satisfeito' : 'nao_satisfeito',
            porque: satisfeito
                ? `a viatura tem «${n.caracteristica}»`
                : `a viatura NÃO tem «${n.caracteristica}»`,
        });
    }
    return {
        requisitos: saida,
        excluir: saida.some((r) => r.resultado === 'nao_satisfeito'),
        indeterminados: saida.filter((r) => r.resultado === 'indeterminado').length,
    };
}
/**
 * Os sinónimos que hoje se sabem — a semente da tabela `sinonimo` (fonte 6.7).
 *
 * ⚠️ **Escritos à mão a partir dos dados MEDIDOS**, não inventados: são os 10 tipos de
 * requisito que o gerador produz contra as 7 características que as viaturas têm. Cada
 * linha diz o que se sabe e o que não se sabe.
 *
 * ⚠️ E **não se completa isto por adivinhação.** As cinco entradas com `null` são pedidos
 * reais para os quais a frota não tem dados — a resposta é a GVA passar a registá-los, não
 * um modelo a decidir que «bagageira ampla» significa `WC`.
 */
export const SINONIMOS_CONHECIDOS = [
    // ✅ Casam exactamente.
    ['wi-fi', 'Wi-Fi', 'viatura'],
    ['wifi', 'Wi-Fi', 'viatura'],
    ['frigorifico', 'frigorífico', 'viatura'],
    ['frigobar', 'frigorífico', 'viatura'],
    // ⚠️ Casam por sinónimo — e a comparação de texto não os apanhava.
    ['wc', 'WC', 'viatura'],
    ['wc a bordo', 'WC', 'viatura'],
    ['casa de banho', 'WC', 'viatura'],
    ['ar condicionado reforcado', 'A/C', 'viatura'],
    ['ar condicionado', 'A/C', 'viatura'],
    ['a/c', 'A/C', 'viatura'],
    ['tv', 'TV', 'viatura'],
    ['televisao', 'TV', 'viatura'],
    ['usb', 'USB', 'viatura'],
    ['carregadores usb', 'USB', 'viatura'],
    ['cama', 'cama', 'viatura'],
    // ✅ AS CINCO QUE A GVA AUTORIZOU ACRESCENTAR (2026-08-06). Eram o buraco do F2: os
    //    pedidos pediam-nas, a frota não as registava, e ficavam todas `indeterminado`.
    ['cintos de 3 pontos', 'cintos_3_pontos', 'viatura'],
    ['cintos 3 pontos', 'cintos_3_pontos', 'viatura'],
    ['bagageira ampla', 'bagageira_ampla', 'viatura'],
    ['bagageira grande', 'bagageira_ampla', 'viatura'],
    ['porao grande', 'bagageira_ampla', 'viatura'],
    ['microfone para guia', 'microfone', 'viatura'],
    ['microfone', 'microfone', 'viatura'],
    ['sonorizacao', 'microfone', 'viatura'],
    ['lugares para cadeira de rodas', 'cadeira_rodas', 'viatura'],
    ['cadeira de rodas', 'cadeira_rodas', 'viatura'],
    ['acessivel', 'cadeira_rodas', 'viatura'],
    // 🔴 2026-10-23 · ESPANHOL e INGLÊS — caso fictício espanhol do dev: «va en silla de ruedas» ia
    //    para as notas em vez de marcar a cadeira de rodas na grelha. Os clientes escrevem nas duas.
    ['silla de ruedas', 'cadeira_rodas', 'viatura'],
    ['wheelchair', 'cadeira_rodas', 'viatura'],
    ['accesible', 'cadeira_rodas', 'viatura'],
    ['accessible', 'cadeira_rodas', 'viatura'],
    ['microfono', 'microfone', 'viatura'],
    ['microphone', 'microfone', 'viatura'],
    ['aire acondicionado', 'A/C', 'viatura'],
    ['air conditioning', 'A/C', 'viatura'],
    ['aseo', 'WC', 'viatura'],
    ['toilet', 'WC', 'viatura'],
    ['double decker', 'dois_pisos', 'viatura'],
    ['autocarro de 2 pisos', 'dois_pisos', 'viatura'],
    ['2 pisos', 'dois_pisos', 'viatura'],
    ['dois pisos', 'dois_pisos', 'viatura'],
    // ⚠️ É do MOTORISTA, não da viatura — e agora **também é verificável**: os motoristas
    //    passaram a ter `competencias`. Continua a não excluir viatura nenhuma, porque uma
    //    viatura não fala inglês. O que faz é **avisar** quando o condutor escolhido não
    //    tem a competência pedida — e isso decide-se na escolha do motorista, não no filtro.
    ['condutor com ingles', 'ingles', 'motorista'],
    ['ingles', 'ingles', 'motorista'],
    ['condutor com espanhol', 'espanhol', 'motorista'],
    ['espanhol', 'espanhol', 'motorista'],
    ['condutor com frances', 'frances', 'motorista'],
    ['frances', 'frances', 'motorista'],
    ['alemao', 'alemao', 'motorista'],
];
/**
 * As competências que o pedido exige do CONDUTOR, já normalizadas.
 *
 * ⚠️ Separadas dos requisitos de viatura de propósito: **nenhuma característica de frota
 * satisfaz «condutor com inglês»**, e tratá-las juntas foi o que fez o F2 declarar este
 * requisito como indeterminado. Agora é verificável — mas contra o MOTORISTA, e só depois
 * de ele estar escolhido.
 */
export function competenciasExigidas(requisitosDoPedido, sinonimos) {
    const fora = [];
    for (const bruto of separarRequisitos(requisitosDoPedido)) {
        const n = normalizarRequisito(bruto, sinonimos);
        if (n.alvo === 'motorista' && n.caracteristica !== null)
            fora.push(n.caracteristica);
    }
    return [...new Set(fora)];
}
/** Constrói a tabela consumível. Ignora as entradas sem característica conhecida. */
export function tabelaDeSinonimos(entradas = SINONIMOS_CONHECIDOS) {
    const m = new Map();
    for (const [texto, caracteristica, alvo] of entradas) {
        if (caracteristica === null)
            continue;
        m.set(normalizarTexto(texto), { caracteristica, alvo });
    }
    return m;
}
//# sourceMappingURL=requisitos.js.map